import Element, { type RootEntity, RootEntityType } from '@/modules/elements/models/Element'
import type Vue from 'vue'
import type { EditorContext } from './tiptap-editor-context'
import { createMentionUsersGetter } from './tiptap-mention-users'
import { handleContentScopedDrop, handleContentScopedPaste } from './tiptap-content-paste'

export type CreateEditorParams = Partial<Omit<EditorContext, 'slashCommands'>> & {
  parent: Vue
  content?: string
  /** Defaults to on */
  enableSlashCommands?: boolean
  enableIframe?: boolean
  variableScopeId?: string
  /** Label in the editor diagnostics; defaults to the parent component's tag */
  diagnosticsKind?: string
  /** A context the caller keeps and rebinds (the editor pool); the host fields above are then ignored */
  ctx?: EditorContext
}

export function editorContextFrom(params: CreateEditorParams): EditorContext {
  return {
    parent: params.parent,
    getUsers: params.getUsers,
    slashCommands: params.enableSlashCommands ?? true,
    handleKeyDown: params.handleKeyDown,
    addElementHandler: params.addElementHandler,
    replaceElementHandler: params.replaceElementHandler,
    embedInline: params.embedInline,
    readOnlyTaskItemChecked: params.readOnlyTaskItemChecked,
    handlePaste: params.handlePaste,
    handleDrop: params.handleDrop,
    rootEntityType: params.rootEntityType,
    slashScope: params.slashScope,
    activationToken: params.activationToken ?? 0
  }
}

export function variableScopeIdForElement(element: Element): string | undefined {
  return element.rootEntityType === RootEntityType.SPACE_PAGE_TEMPLATE ? element.rootEntityId : undefined
}

/**
 * A table cell's editor options: the content-scope slash menu (text blocks only) and mentions. Paste and drop stay
 * as they were, since cells accept embeds and canvas elements that content-scoped blocks refuse.
 */
export function tableCellEditorParams(element: Element) {
  return {
    enableSlashCommands: true,
    slashScope: 'content' as const,
    getUsers: createMentionUsersGetter(element),
    rootEntityType: element.rootEntityType,
    variableScopeId: variableScopeIdForElement(element),
    embedInline: true
  }
}

/** The same for a pooled cell. No parent or activationToken: the pool owns both and rebinds them per target. */
export function tableCellEditorCtx(element: Element): Partial<EditorContext> {
  const { enableSlashCommands, slashScope, getUsers, rootEntityType, embedInline } = tableCellEditorParams(element)
  return { slashCommands: enableSlashCommands, slashScope, getUsers, rootEntityType, embedInline }
}

/** Shared `createEditor` options for nested proposal block rich-text (content slash + mentions). */
export function contentScopedBlockEditorParams(
  parent: Vue,
  element: Element,
  rootEntity?: RootEntity
): Pick<
  CreateEditorParams,
  | 'parent'
  | 'enableSlashCommands'
  | 'slashScope'
  | 'getUsers'
  | 'rootEntityType'
  | 'variableScopeId'
  | 'handlePaste'
  | 'handleDrop'
> {
  const slashScope = 'content' as const
  return {
    parent,
    enableSlashCommands: true,
    slashScope,
    getUsers: createMentionUsersGetter(element, rootEntity),
    rootEntityType: element.rootEntityType,
    variableScopeId: variableScopeIdForElement(element),
    handlePaste: (view, event, slice) => handleContentScopedPaste({ slashScope }, view, event, slice),
    handleDrop: (view, event, slice, moved) => handleContentScopedDrop({ slashScope }, view, event, slice, moved)
  }
}
